import { doc, type DocumentReference } from 'firebase/firestore';
import { DEFAULT_NURSING_LAYOUT_TITLES } from '../lib/crossAuditSlot';
import {
  departmentRequiresNursingCrossAudit,
  getDefaultNursingLayoutTitlesForDepartment,
  getRequiredNursingChartSlots,
} from '../lib/nursingDepartmentPolicy';
import {
  makeSubmissionDraftKey,
  readDraftFromLocalStorage,
  submissionDraftMatchesScope,
  writeDraftToLocalStorage,
  type SubmissionDocument,
} from '../services/submissions';
import { computeChartCountFromData, normalizeSubmissionShape } from '../services/submissionShape';
import { REQUIRED_MED_ERROR_CHARTS } from '../lib/compliance_constants';
import { NURSING_NOTE_IDENTIFIERS } from '../hooks/useComplianceCategories';
import {
  DEFAULT_COMPLIANCE_CATEGORIES,
  getCategoryById,
  type ComplianceCategory,
} from '../lib/complianceCategories';
import { type ChecklistSection } from '../lib/checklists';
import {
  hasDepartmentCategoriesConfigured,
  normalizeDepartmentCategories,
  type Department,
} from '../lib/departments';
import { db } from '../lib/firestore';
import { getReportingMonth } from '../lib/reportingMonth';

// Lazy palette access to avoid TDZ issues - use getPalette() instead
// Define the palette inline to avoid any import/initialization order issues
// These values match compliancePalette in complianceCategoryTypes.ts
export const getPalette = () => ({
  primary: '#6F4CF6',
  green: '#22C55E',
  amber: '#F59E0B',
  red: '#EF4444',
  blue: '#3B82F6',
} as const);
export const SUPERVISOR_REPORTING_TAB = "รายงานรวม";
export const TEAM_RESULTS_TAB = "คณะทำงาน";
export const TG_PROJECT_TAB = "โครงการ TG";
export const KPIS_NURSING_TAB = "KPIs ฝ่ายการพยาบาล";
export const LAUNCH_CATEGORY_TITLE = "บันทึกทางการพยาบาล";
/** XLSX import panel: opt-in only. Set VITE_NURSE_NOTES_IMPORT_ENABLED=true to show (server still needs callables + param). */
const NURSE_NOTES_XLSX_IMPORT_UI_ENABLED =
  import.meta.env.VITE_NURSE_NOTES_IMPORT_ENABLED === "true";
export const NEEDLE_AND_SHARP_TITLE = "เข็มตำ/ของมีคมบาด";
export const MED_ERROR_TITLE = "นิเทศระบบยา";
export const SIREN_BUNDLE_TITLE = "SIREN bundle";
const shouldDebugHydration = () =>
  import.meta.env.DEV || (typeof window !== "undefined" && localStorage.getItem("debugHydration") === "1");

/** Safe for template literals, JSX, and console — avoids ToPrimitive throws on exotic .message values. */
const toSafeErrorDisplayString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value === undefined || value === null) return "Unknown error";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return toSafeErrorDisplayString(value.message);
  try {
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  } catch {
    return "Unknown error";
  }
};

const compactErrorSummary = (error: unknown) => {
  const errorCode = typeof error === "object" && error && "code" in error ? (error as { code?: unknown }).code : undefined;
  const rawMessage =
    typeof error === "object" && error && "message" in error ? (error as { message?: unknown }).message : undefined;
  const errorMessage =
    rawMessage !== undefined ? toSafeErrorDisplayString(rawMessage) : toSafeErrorDisplayString(error);
  const errorName =
    typeof error === "object" && error && "name" in error ? (error as { name?: unknown }).name : "unknown";
  const stackPreview =
    typeof error === "object" && error && "stack" in error && typeof (error as { stack?: unknown }).stack === "string"
      ? ((error as { stack: string }).stack.split("\n").slice(0, 2).join(" | ") || null)
      : null;

  return {
    errorCode: typeof errorCode === "string" ? errorCode : undefined,
    errorMessage,
    errorName: typeof errorName === "string" ? errorName : "unknown",
    stackPreview,
  };
};

const getArrayLength = (value: unknown) => (Array.isArray(value) ? value.length : 0);

const compactSubmitCounters = (snapshot: SubmissionDocument, chartIdx: number) => {
  const answersRow = Array.isArray(snapshot.answers) ? snapshot.answers[chartIdx] : [];
  const historyRow = Array.isArray(snapshot.history) ? snapshot.history[chartIdx] : [];

  return {
    chartIndex: chartIdx,
    answersRows: getArrayLength(snapshot.answers),
    answersInCurrentChart: getArrayLength(answersRow),
    diseaseCount: getArrayLength(snapshot.disease),
    focusCount: getArrayLength(snapshot.focus),
    treatmentsCount: getArrayLength(snapshot.treatments),
    problemsCount: getArrayLength(snapshot.problems),
    suggestionsCount: getArrayLength(snapshot.suggestions),
    datesCount: getArrayLength(snapshot.dates),
    shiftsCount: getArrayLength(snapshot.shifts),
    hnCount: getArrayLength(snapshot.hn),
    historyRows: getArrayLength(snapshot.history),
    historyItemsInCurrentChart: getArrayLength(historyRow),
    supervisionRecordsCount: getArrayLength(snapshot.supervisionRecords),
    chartTitlesCount: getArrayLength(snapshot.chartTitles),
    submittedCount: getArrayLength(snapshot.submitted),
    incompleteCount: getArrayLength(snapshot.incomplete),
    versionsCount: getArrayLength(snapshot.versions),
  };
};
export const DEVELOPMENT_ONLY_TITLES = new Set<string>();
export const GRAY_ICON_TITLES = new Set<string>();
export const CARD_WARNING_TITLES = new Set([LAUNCH_CATEGORY_TITLE, MED_ERROR_TITLE]);
export const INACTIVE_ICON_COLOR = "#94A3B8";

export const resolveSubmissionDepartmentRef = ({
  isSupervisorNurseRole,
  selectedDepartmentRef,
  submissionDepartmentId,
}: {
  isSupervisorNurseRole: boolean;
  selectedDepartmentRef: DocumentReference | null;
  submissionDepartmentId: string | null;
}) => {
  if (isSupervisorNurseRole) return null;
  if (selectedDepartmentRef) return selectedDepartmentRef;
  if (submissionDepartmentId) return doc(db, "departments", submissionDepartmentId);
  return null;
};

export const formatPeriodicityLabel = (periodicity?: string) => {
  switch (periodicity?.toLowerCase()) {
    case 'weekly':
      return 'รายสัปดาห์';
    case 'quarterly':
      return 'รายไตรมาส';
    case 'yearly':
      return 'รายปี';
    case 'monthly':
      return 'รายเดือน';
    default:
      return periodicity || 'Monthly compliance';
  }
};

const SUPERVISION_RECIPIENT_OPTIONS = [
  { value: "individual", label: "รายบุคคล" },
  { value: "custom", label: "ระบุ" },
  { value: "group", label: "รายกลุ่ม" },
];

const SUPERVISION_ACTIVITY_OPTIONS = [
  { value: "rounds", label: "การเยี่ยมตรวจทางการพยาบาล (nursing rounds)" },
  { value: "conference", label: "การประชุมปรึกษาทางการพยาบาล (nursing conference)" },
  { value: "teaching", label: "การสอน (teaching)" },
  { value: "problem-solving", label: "การแก้ปัญหา (problem-solving)" },
  { value: "counseling", label: "การให้คำปรึกษา" },
  { value: "observation", label: "การสังเกต (observation)" },
  { value: "collaboration", label: "การร่วมมือปฏิบัติงาน" },
];

const SUPERVISION_ISSUE_OPTIONS = [
  { value: "manpower", label: "การจัดอัตรากำลัง" },
  { value: "assignment", label: "การมอบหมายงาน" },
  { value: "equipment", label: "อุปกรณ์เครื่องมือเครื่องใช้" },
  { value: "environment", label: "สถานที่สิ่งแวดล้อมและ 5 ส" },
  { value: "energy", label: "อนุรักษ์พลังงาน" },
  { value: "disease-standard", label: "มาตรฐานการดูแลผู้ป่วยรายโรค" },
  { value: "medication", label: "การบริหารยา" },
  { value: "blood", label: "การบริหารเลือด" },
  { value: "identification", label: "การบ่งชี้" },
  { value: "specimen", label: "การเก็บสิ่งส่งตรวจ" },
  { value: "fall", label: "การป้องกันFall" },
  { value: "needleandsharp", label: "การป้องกันเข็มทิ่มตำ" },
  { value: "phlebitis", label: "การป้องกัน Phlebitis" },
  { value: "pre-op", label: "การเตรียมผู้ป่วยส่งทำผ่าตัด /หัตถการ" },
  { value: "isitdown", label: "ISITDOWN" },
  { value: "nurse_notes", label: "บันทึกทางการพยาบาล" },
  { value: "ic", label: "IC" },
  { value: "service", label: "พฤติกรรมบริการ" },
  { value: "attire", label: "การแต่งกาย" },
  { value: "self-development", label: "การอบรมพัฒนาตนเอง" },
  { value: "staff-health", label: "การส่งเสริมสขภาพกายใจเจ้าหน้าที่" },
  { value: "patient-education", label: "การสอนแนะนำผู้ป่วย" },
  { value: "other", label: "อื่นๆระบุ......" },
];

type SupervisionRecord = {
  recipients: string[];
  recipientsDetail: string;
  activities: string[];
  issues: string[];
  issueOther: string;
  problemDetail: string;
  solution: string;
  followUp: string;
};

type SupervisionErrorState = {
  recipients: boolean;
  recipientsDetail: boolean;
  activities: boolean;
  issues: boolean;
  issueOther: boolean;
  problemDetail: boolean;
  solution: boolean;
  followUp: boolean;
  date: boolean;
};

export const createSupervisionRecord = (): SupervisionRecord => ({
  recipients: [],
  recipientsDetail: "",
  activities: [],
  issues: [],
  issueOther: "",
  problemDetail: "",
  solution: "",
  followUp: "",
});

const createSupervisionErrorState = (): SupervisionErrorState => ({
  recipients: false,
  recipientsDetail: false,
  activities: false,
  issues: false,
  issueOther: false,
  problemDetail: false,
  solution: false,
  followUp: false,
  date: false,
});

const sanitizeStringArray = (values?: Array<string | undefined>) =>
  Array.isArray(values) ? values.map((val) => val ?? "") : [];

export const isDocumentReference = (value: unknown): value is DocumentReference => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === "string" && v.id.trim().length > 0;
};

const sanitizeSupervisionRecord = (record?: SupervisionRecord | null): SupervisionRecord => {
  const safeRecord = record ?? createSupervisionRecord();
  return {
    recipients: sanitizeStringArray(safeRecord.recipients),
    recipientsDetail: safeRecord.recipientsDetail ?? "",
    activities: sanitizeStringArray(safeRecord.activities),
    issues: sanitizeStringArray(safeRecord.issues),
    issueOther: safeRecord.issueOther ?? "",
    problemDetail: safeRecord.problemDetail ?? "",
    solution: safeRecord.solution ?? "",
    followUp: safeRecord.followUp ?? "",
  };
};

const DEFAULT_CHARTS = 10;
const DEFAULT_CHOICE = "NA";
const CHOICE_VALUES = new Set(["1", "0", DEFAULT_CHOICE]);

const sanitizeChoice = (val?: string) => (val && CHOICE_VALUES.has(val) ? val : DEFAULT_CHOICE);
export const hasNonDefaultChoice = (val?: string) => sanitizeChoice(val) !== DEFAULT_CHOICE;

/** Returns true if every criterion has a valid choice (1, 0, or NA). */
const hasAllCriteriaFilled = (answersRow: string[] | undefined): boolean => {
  if (!answersRow || answersRow.length === 0) return false;
  return answersRow.every((v) => {
    const s = typeof v === "string" ? v.trim() : String(v ?? "");
    return s.length > 0 && CHOICE_VALUES.has(s);
  });
};

/**
 * Sidebar / card progress: a chart must not count as "draft-complete" when every cell is still the default NA
 * (initial state looks "filled" but the user has not chosen 1 or 0 anywhere). Submitted charts still count via `submitted[]`.
 */
export function hasNursingDraftRowCompleteForProgress(answersRow: string[] | undefined): boolean {
  if (!hasAllCriteriaFilled(answersRow)) return false;
  return (answersRow as string[]).some((v) => hasNonDefaultChoice(v));
}

/**
 * Launch-card % for nursing: count submitted or draft-complete charts in **any** tab order
 * (canonical requirement = 8). Prefix-only counting breaks when ตรวจไขว้ / added charts
 * sit at the end while earlier placeholders stay empty — same data showed ~71% with 8 real submits.
 * Denominator stays `requiredChartSlots` (8); numerator is min(doneAcrossAllTabs, required).
 */
export function computeNursingCardProgress(
  chartCount: number,
  submitted: boolean[],
  filled: boolean[],
  requiredChartSlots?: number,
): number {
  if (!Number.isFinite(chartCount) || chartCount <= 0) return 0;
  const reqCandidate = requiredChartSlots ?? chartCount;
  const denom =
    Number.isFinite(reqCandidate) && reqCandidate > 0 ? Math.floor(reqCandidate) : chartCount;
  let done = 0;
  for (let i = 0; i < chartCount; i += 1) {
    if (submitted[i] || filled[i]) done += 1;
  }
  return Math.round((Math.min(done, denom) / denom) * 100);
}

type ChecklistDisplaySection = ChecklistSection & { rows: { label: string; index: number }[] };

const getRequiredNursingCharts = () => getCategoryById("nurse_notes")?.charts ?? 8;

export function getCategoryConfig(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  if (!title) return undefined;
  return categories.find((category) => category.title === title || category.id === title);
}

/** Resolve display title to canonical category doc ID. When adding new category aliases,
 * update COMPLIANCE_CATEGORY_ALIASES in canonicalIds.ts and ensure category config aligns. */
export function resolveCategoryDocId(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  if (!title) return "";
  const normalized = title.trim();
  if (!normalized) return "";
  const match = categories.find((category) => category.id === normalized || category.title === normalized);
  return match?.id ?? normalized;
}

export function getChartCount(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.charts ?? DEFAULT_CHARTS;
}

export function getPeriodLabel(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.periodLabel || "ครั้งที่";
}

export function allowsExtraComplianceCharts(title?: string): boolean {
  return title === LAUNCH_CATEGORY_TITLE || title === "นิเทศทางการพยาบาล" || title === MED_ERROR_TITLE;
}

export function extraComplianceChartAddButtonLabel(title?: string, periodLabel = "ครั้งที่"): string {
  if (title === MED_ERROR_TITLE) return "+ เพิ่มครั้ง";
  return `+ เพิ่ม ${periodLabel} ใหม่`;
}

export function requiredComplianceChartSlots(title?: string, departmentId?: string | null): number {
  if (title === LAUNCH_CATEGORY_TITLE) return getRequiredNursingChartSlots(departmentId);
  if (title === MED_ERROR_TITLE) return REQUIRED_MED_ERROR_CHARTS;
  return 1;
}

export function trimPaddedOptionalChartCount(
  storedCount: number,
  requiredCount: number,
  isSlotOccupied: (index: number) => boolean,
): number {
  const required = Number.isFinite(requiredCount) && requiredCount > 0 ? Math.floor(requiredCount) : 1;
  if (!Number.isFinite(storedCount) || storedCount <= 0) return required;
  const count = Math.floor(storedCount);
  if (count <= required) return required;
  let lastOccupied = required - 1;
  for (let i = required; i < count; i += 1) {
    if (isSlotOccupied(i)) lastOccupied = i;
  }
  return lastOccupied + 1;
}

export function isOccupiedMedErrorSlot(answersRow: unknown, submitted: unknown, problemsRow: unknown): boolean {
  if (submitted === true) return true;
  if (Array.isArray(answersRow) && answersRow.some((v) => hasNonDefaultChoice(v == null ? "" : String(v)))) {
    return true;
  }
  if (Array.isArray(problemsRow) && problemsRow.some((v) => typeof v === "string" && v.trim().length > 0)) {
    return true;
  }
  return false;
}

export function resolveMedErrorVisibleChartCount(
  data: Record<string, unknown> | null | undefined,
  fallback: number = REQUIRED_MED_ERROR_CHARTS,
): number {
  const storedCount = computeChartCountFromData(data, fallback);
  if (!data) return REQUIRED_MED_ERROR_CHARTS;
  const { answers } = normalizeSubmissionShape(data);
  const submitted = Array.isArray(data.submitted) ? data.submitted : [];
  const problems = Array.isArray(data.problems) ? data.problems : [];
  return trimPaddedOptionalChartCount(storedCount, REQUIRED_MED_ERROR_CHARTS, (i) =>
    isOccupiedMedErrorSlot(answers[i], submitted[i], problems[i]),
  );
}

export function getTeamLabel(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.teamLabel || "";
}

export function computeProgressPercent(completed: number, total: number) {
  if (!Number.isFinite(completed) || !Number.isFinite(total) || total <= 0) return 0;
  const safeCompleted = Math.max(0, Math.min(completed, total));
  return Math.round((safeCompleted / total) * 100);
}

export type ChecklistCard = {
  id: number;
  title: string;
  categoryDocId: string;
  subtitle: string;
  progress: number;
  color: string;
  warning?: boolean;
  alertText?: string;
  iconColor?: string;
};

// Lazy initialization to avoid TDZ issues - convert to function
const getBaseCards = (): ChecklistCard[] => {
  return DEFAULT_COMPLIANCE_CATEGORIES.map((category, index) => ({
    id: index + 1,
    title: category.title,
    categoryDocId: category.id,
    subtitle: "Monthly compliance",
    progress: 0,
    color: category.color ?? getPalette().primary,
  }));
};

// Remove duplicate export - BASE_COMPLIANCE_CARD_TITLES is already exported from ComplianceHub.logic.ts
// Use getBaseCards() function instead of baseCards constant

export const computeAllowedCategories = (
  scopedDepartments: Department[],
  hasWildcardDepartmentScope: boolean,
  isAdmin: boolean,
  complianceCategories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES,
  currentUserId?: string | null,
  scopedTeamIds: string[] = [],
  _hasNNTeamRole: boolean = false,
  teamAllowedCategoryIds: string[] = [],
) => {
  const scopedDepartmentIds = new Set(scopedDepartments.map((dept) => dept.id));
  const allowedTeamIds = scopedTeamIds.filter(Boolean);
  const hasWildcardTeamScope = allowedTeamIds.includes('*');
  const activeCategories = complianceCategories.filter((category) => category.active !== false);

  const categories = scopedDepartments.flatMap((dept) => normalizeDepartmentCategories(dept, activeCategories));
  const normalizedCategories = Array.from(new Set(categories));

  const hasConfiguredCategories = scopedDepartments.some(hasDepartmentCategoriesConfigured);

  const shouldFallbackToAllCategories = !hasConfiguredCategories && (hasWildcardDepartmentScope || isAdmin);

  const baseCategories: ComplianceCategory[] = (() => {
    if (!scopedDepartments.length) {
      return shouldFallbackToAllCategories
        ? activeCategories
        : activeCategories.filter((category) => !category.departmentIds?.length);
    }

    if (shouldFallbackToAllCategories) {
      return activeCategories;
    }

    return normalizedCategories.map(
      (ref) =>
        activeCategories.find((category) => category.id === ref || category.title === ref) ?? {
          id: ref,
          title: ref,
        },
    );
  })();

  const allowedTitles = baseCategories
    .filter((category) => {
      const matchesDepartmentScope =
        !category.departmentIds?.length || category.departmentIds.some((id) => scopedDepartmentIds.has(id));
      const matchesUserScope = !category.userIds?.length || (currentUserId ? category.userIds.includes(currentUserId) : false);
      const matchesTeamScope = hasWildcardTeamScope || !category.teamId || allowedTeamIds.includes(category.teamId);
      return category.active !== false && matchesDepartmentScope && matchesUserScope && matchesTeamScope;
    })
    .map((category) => category.title)
    .filter((title): title is string => Boolean(title?.trim()));

  const allowedSet = new Set(allowedTitles);

  // Non-admins always need nursing notes + นิเทศระบบยา on the compliance hub
  // (team NN / med_error roles are separate from normal-page scope).
  if (!isAdmin) {
    complianceCategories
      .filter((category) =>
        NURSING_NOTE_IDENTIFIERS.has(category.id) ||
        NURSING_NOTE_IDENTIFIERS.has(category.title ?? "") ||
        category.id === "med_error" ||
        category.title === MED_ERROR_TITLE ||
        category.id === "siren_bundle" ||
        category.title === SIREN_BUNDLE_TITLE ||
        category.id === "needleandsharp" ||
        category.title === NEEDLE_AND_SHARP_TITLE,
      )
      .forEach((category) => {
        if (category.title?.trim()) {
          allowedSet.add(category.title);
        }
      });
  }

  // Add team-allowed categories (e.g. ethic) for users with teamAllowedCategoryIds
  const teamCategorySet = new Set(teamAllowedCategoryIds.filter(Boolean));
  if (teamCategorySet.size > 0) {
    complianceCategories
      .filter(
        (category) =>
          teamCategorySet.has(category.id ?? "") || teamCategorySet.has(category.title ?? ""),
      )
      .forEach((category) => {
        if (category.title?.trim()) {
          allowedSet.add(category.title);
        }
      });
  }

  return Array.from(allowedSet.values());
};

export function makeArray<T>(len: number, factory: (i: number) => T) {
  return Array.from({ length: len }, (_, i) => factory(i));
}

const NURSING_CHART_TYPES = ["รายโรค", "โรคทั่วไป", "ตรวจไขว้"] as const;
export type NursingChartType = (typeof NURSING_CHART_TYPES)[number];

/** Cloned from {@link DEFAULT_NURSING_LAYOUT_TITLES} — keep hydration merge and UI defaults in sync. */
const DEFAULT_NURSING_CHART_TITLES: string[] = [...DEFAULT_NURSING_LAYOUT_TITLES];

export const getNursingChartTypeFromTitle = (title: string): NursingChartType | null => {
  const match = NURSING_CHART_TYPES.find((type) => title.includes(type));
  return match || null;
};

/** Department-aware title pool used when normalizing sparse or short title arrays. */
export const getNursingChartTitleFallbackPool = (
  departmentId?: string | null,
): string[] => {
  if (departmentId && !departmentRequiresNursingCrossAudit(departmentId)) {
    return [...getDefaultNursingLayoutTitlesForDepartment(departmentId)];
  }
  return DEFAULT_NURSING_CHART_TITLES;
};

export const normalizeNursingChartTitles = (
  titles: string[],
  count: number,
  fallbackTitles: string[] = DEFAULT_NURSING_CHART_TITLES,
) => {
  const counters: Record<NursingChartType, number> = {
    รายโรค: 0,
    โรคทั่วไป: 0,
    ตรวจไขว้: 0,
  };

  const pool = fallbackTitles.length > 0 ? fallbackTitles : DEFAULT_NURSING_CHART_TITLES;

  return makeArray(count, (i) => {
    const defaultTitle = pool[i] ?? pool[pool.length - 1] ?? "Chart โรคทั่วไป";
    const baseTitle = titles[i] || defaultTitle;
    const chartType = getNursingChartTypeFromTitle(baseTitle) || "โรคทั่วไป";
    const nextNumber = counters[chartType] + 1;
    counters[chartType] = nextNumber;
    return `Chart ${chartType} ${nextNumber}`;
  });
};

export const getDefaultNursingChartTitles = (
  count: number,
  departmentId?: string | null,
) => {
  const fallbackTitles = getNursingChartTitleFallbackPool(departmentId);
  return normalizeNursingChartTitles(fallbackTitles, count, fallbackTitles);
};

export const getNextNursingChartTitle = (type: NursingChartType, existingTitles: string[]) => {
  const counters: Record<NursingChartType, number> = {
    รายโรค: 0,
    โรคทั่วไป: 0,
    ตรวจไขว้: 0,
  };

  existingTitles.forEach((title) => {
    const detectedType = getNursingChartTypeFromTitle(title);
    if (detectedType) counters[detectedType] += 1;
  });

  const nextNumber = counters[type] + 1;
  return `Chart ${type} ${nextNumber}`;
};

const normalizeSupervisionRecord = (input: any): SupervisionRecord => {
  if (!input || typeof input !== "object") return createSupervisionRecord();
  const record = input as Partial<SupervisionRecord>;
  return {
    recipients: Array.isArray(record.recipients)
      ? (record.recipients.filter((v) => typeof v === "string") as string[])
      : [],
    recipientsDetail: typeof record.recipientsDetail === "string" ? record.recipientsDetail : "",
    activities: Array.isArray(record.activities)
      ? (record.activities.filter((v) => typeof v === "string") as string[])
      : [],
    issues: Array.isArray(record.issues) ? (record.issues.filter((v) => typeof v === "string") as string[]) : [],
    issueOther: typeof record.issueOther === "string" ? record.issueOther : "",
    problemDetail: typeof record.problemDetail === "string" ? record.problemDetail : "",
    solution: typeof record.solution === "string" ? record.solution : "",
    followUp: typeof record.followUp === "string" ? record.followUp : "",
  };
};

export const normalizeSupervisionRecords = (source: any, count: number) =>
  makeArray(count, (i) => normalizeSupervisionRecord(Array.isArray(source) ? source[i] : undefined));

export const getCurrentMonthKey = (title: string, date = new Date(), departmentId: string | null = null, userId: string | null = null) =>
  makeSubmissionDraftKey(departmentId, title, getReportingMonth(date), userId);

export function normalizeArray<T>(source: T[] | undefined, count: number, filler: T) {
  const safe = Array.isArray(source) ? source : [];
  return makeArray(count, (i) => (safe[i] !== undefined ? (safe[i] as T) : filler));
}

function normalizeStringMatrix(source: any, rows: number, cols: number) {
  return makeArray(rows, (i) => {
    const row = Array.isArray(source?.[i]) ? (source[i] as any[]) : [];
    return makeArray(cols, (j) => {
      const val = row[j];
      if (typeof val === "string") return val;
      if (typeof val === "number") return String(val);
      return "";
    });
  });
}

export function readDraftWithFallback(monthKey: string, legacyMonthKey: string, expectedDeptId: string | null = null) {
  if (typeof window === "undefined") return { data: null };
  const rawPrimary = readDraftFromLocalStorage(monthKey);
  if (rawPrimary) {
    if (submissionDraftMatchesScope(rawPrimary, expectedDeptId)) {
      return { data: rawPrimary };
    }
    if (expectedDeptId?.trim()) {
      try {
        localStorage.removeItem(monthKey);
      } catch {
        /* ignore */
      }
    }
  }
  if (!legacyMonthKey || legacyMonthKey === monthKey) return { data: null };
  const rawLegacy = readDraftFromLocalStorage(legacyMonthKey);
  if (!rawLegacy) return { data: null };
  if (!submissionDraftMatchesScope(rawLegacy, expectedDeptId)) {
    if (expectedDeptId?.trim()) {
      try {
        localStorage.removeItem(legacyMonthKey);
      } catch {
        /* ignore */
      }
    }
    return { data: null };
  }
  writeDraftToLocalStorage(monthKey, rawLegacy, Boolean((rawLegacy as { draft?: boolean }).draft));
  return { data: rawLegacy, migrated: true };
}

export function getStoredChartCount(monthKey: string, legacyMonthKey: string, fallback: number, expectedDeptId: string | null = null) {
  if (typeof window === "undefined") return fallback;
  const { data } = readDraftWithFallback(monthKey, legacyMonthKey, expectedDeptId);
  if (!data) return fallback;
  return computeChartCountFromData(data, fallback);
}
